import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Eye, EyeOff } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { useToast } from '../contexts/ToastContext'
import { GoogleLogin } from '@react-oauth/google'
import AppLogo from '../components/AppLogo'
import Loader from '../components/Loader'

function Login() {
  const [formData, setFormData] = useState({ email: '', password: '' })
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const { login, googleLogin } = useAuth()
  const toast = useToast()

  const handleGoogleLogin = async (token) => {
    setIsLoading(true);
    const result = await googleLogin(token);
    if (!result.success) {
      toast.error(result.error || 'Google login failed');
    }
    setIsLoading(false);
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!formData.email || !formData.password) {
      toast.error('Please fill in all fields')
      return
    }

    setIsLoading(true)

    const result = await login(formData.email, formData.password)

    if (!result.success) {
      // Show error toast notification
      toast.error(result.error, 6000) // Show for 6 seconds
    }

    setIsLoading(false)
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-900 px-4">
      <div className="max-w-md w-full space-y-6">
        <div className="card-gradient p-8 rounded-xl">
          <div className="text-center mb-6">
            <AppLogo size="large" showImage />
            <h2 className="text-3xl font-bold text-white mt-4">Welcome back</h2>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <input
              name="email"
              type="email"
              placeholder="Email"
              className="input w-full"
              value={formData.email}
              onChange={handleChange}
              disabled={isLoading}
            />

            <div className="relative">
              <input
                name="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Password"
                className="input w-full pr-12"
                value={formData.password}
                onChange={handleChange}
                disabled={isLoading}
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2"
                onClick={() => setShowPassword(v => !v)}
              >
                {showPassword ? <EyeOff /> : <Eye />}
              </button>
            </div>

            <div className="flex items-center justify-end">
              <Link
                to="/forgot-password"
                className="text-sm text-primary-400 hover:text-primary-300 transition-colors"
              >
                Forgot password?
              </Link>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary w-full flex items-center justify-center p-3" // added flex, items-center, justify-center and p-3 for consistency
            >
              {isLoading ? (
                <>
                  <Loader size="sm" variant="white" className="mr-2" />
                  Signing in...
                </>
              ) : 'Sign In'}
            </button>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-700"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-gray-800 text-gray-400">Or continue with</span>
              </div>
            </div>

            <div className="flex justify-center">
              <GoogleLogin
                onSuccess={credentialResponse => {
                  handleGoogleLogin(credentialResponse.credential);
                }}
                onError={() => {
                  toast.error('Google Login Failed');
                }}
                theme="filled_black"
                shape="pill"
                width="100%"
              />
            </div>

            <p className="text-sm text-center text-gray-400 mt-6">
              Don’t have an account?{' '}
              <Link to="/register" className="text-primary-400">Sign up</Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  )
}

export default Login
